import { useState, useCallback, useEffect, useMemo } from "react";
import { request } from "../../../../utils/api";
import { getSessionTenantId } from "../../../../utils/tenantContext";
import { toast } from "react-hot-toast";
import { useUrlTab } from "../../../../hooks/useUrlTab";
import {
  DEFAULT_GENERAL_FORM_TAB,
  GENERAL_FORMS_CATALOG,
  VISIBLE_GENERAL_FORM_TAB_IDS,
  getGeneralFormById,
} from "../generalFormsCatalog";

const PAGE_LIMIT = 10;

const emptyPageState = () => ({
  rows: [],
  loading: false,
  page: 1,
  totalPages: 1,
  total: 0,
  limit: PAGE_LIMIT,
});

/**
 * Catalog-driven list state for General Forms.
 * Only visible forms are fetched; hidden forms stay registered for future enablement.
 */
export const useGeneralFormsList = () => {
  const visibleTabIds = useMemo(
    () => Array.from(VISIBLE_GENERAL_FORM_TAB_IDS),
    [],
  );
  const [activeTab, setActiveTab] = useUrlTab(
    visibleTabIds,
    DEFAULT_GENERAL_FORM_TAB,
  );
  const [pages, setPages] = useState(() => {
    const init = {};
    GENERAL_FORMS_CATALOG.forEach((form) => {
      init[form.id] = emptyPageState();
    });
    return init;
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const getTenantId = () => {
    const fromSession = getSessionTenantId();
    if (fromSession) return fromSession;
    try {
      const userStr = localStorage.getItem("currentUser");
      if (userStr) {
        const user = JSON.parse(userStr);
        const id = user.tenantId ?? user.tenant?.id;
        if (id) return Number(id);
      }
    } catch {
      /* ignore */
    }
    return null;
  };

  const fetchFormPage = useCallback(async (formId, page = 1, signal) => {
    const formDef = getGeneralFormById(formId);
    if (!formDef?.visible) return;

    setPages((prev) => ({
      ...prev,
      [formId]: { ...prev[formId], loading: true },
    }));

    try {
      const tenantId = getTenantId();
      if (!tenantId) {
        toast.error("Tenant context is missing. Please sign in again.");
        setPages((prev) => ({
          ...prev,
          [formId]: { ...prev[formId], loading: false },
        }));
        return;
      }
      const response = await request(
        `/${formDef.apiPath}?page=${page}&limit=${PAGE_LIMIT}&tenantId=${tenantId}`,
        signal ? { signal } : {},
      );
      if (signal?.aborted) return;

      if (response.status === 1) {
        const result = response.result || {};
        const rows = result[formDef.listKey] || result.items || [];
        setPages((prev) => ({
          ...prev,
          [formId]: {
            rows: Array.isArray(rows) ? rows : [],
            loading: false,
            page: result.page || page,
            totalPages: result.totalPages || 1,
            total: result.total || 0,
            limit: result.limit || PAGE_LIMIT,
          },
        }));
      } else {
        toast.error(response.message || `Failed to fetch ${formDef.label}`);
        setPages((prev) => ({
          ...prev,
          [formId]: { ...prev[formId], loading: false },
        }));
      }
    } catch (error) {
      if (signal?.aborted || error?.name === "AbortError") return;
      console.error(`Error fetching ${formId}:`, error);
      toast.error(error?.message || `Error loading ${formDef.label}`);
      setPages((prev) => ({
        ...prev,
        [formId]: { ...prev[formId], loading: false },
      }));
    }
  }, []);

  const setPage = useCallback((formId, page) => {
    setPages((prev) => ({
      ...prev,
      [formId]: { ...prev[formId], page },
    }));
  }, []);

  const activePage = pages[activeTab] || emptyPageState();

  useEffect(() => {
    if (!VISIBLE_GENERAL_FORM_TAB_IDS.has(activeTab)) return undefined;
    const controller = new AbortController();
    fetchFormPage(activeTab, activePage.page, controller.signal);
    return () => controller.abort();
    // intentional: refetch when tab or page changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activePage.page, fetchFormPage]);

  const confirmDelete = useCallback((id, type) => {
    setItemToDelete({ id, type });
    setIsDeleteModalOpen(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!itemToDelete) return;
    const { id, type } = itemToDelete;
    const formDef = getGeneralFormById(type);
    if (!formDef) return;

    try {
      const response = await request(`/${formDef.apiPath}/${id}`, {
        method: "DELETE",
      });

      if (response.status === 1) {
        toast.success(`${formDef.label} deleted successfully`);
        setIsDeleteModalOpen(false);
        setItemToDelete(null);
        const currentPage = pages[type]?.page || 1;
        fetchFormPage(type, currentPage);
      } else {
        toast.error(response.message || `Failed to delete ${formDef.label}`);
      }
    } catch (error) {
      console.error(`Error deleting ${formDef.label}:`, error);
      toast.error(`Error deleting ${formDef.label}`);
    }
  }, [itemToDelete, pages, fetchFormPage]);

  const activeForm = useMemo(
    () => getGeneralFormById(activeTab),
    [activeTab],
  );

  const applyMarks = useCallback(
    async (formIds, markType, action = "apply") => {
      const formDef = getGeneralFormById(activeTab);
      if (!formDef?.hasFlags) {
        toast.error("Flags are not available for this form");
        return null;
      }
      const ids = (formIds || []).map((id) => Number(id)).filter((n) => n > 0);
      if (!ids.length) {
        toast.error("Select at least one form");
        return null;
      }
      const response = await request(`/${formDef.apiPath}/marks`, {
        method: "POST",
        body: JSON.stringify({ markType, action, formIds: ids }),
      });
      if (response.status === 1) {
        fetchFormPage(activeTab, pages[activeTab]?.page || 1);
        return response.result || response;
      }
      throw new Error(response.message || "Failed to update flags");
    },
    [activeTab, fetchFormPage, pages],
  );

  return {
    activeTab,
    setActiveTab,
    activeForm,
    rows: activePage.rows,
    loading: activePage.loading,
    page: activePage.page,
    setPage: (nextPage) => setPage(activeTab, nextPage),
    totalPages: activePage.totalPages,
    total: activePage.total,
    limit: activePage.limit,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    confirmDelete,
    handleDelete,
    applyMarks,
    refresh: () => fetchFormPage(activeTab, activePage.page),
  };
};
